import { fileUpload } from "directus/src/operations";
import React, { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import MultiFileUpload from "ui/components/multi-file-upload";
import HeaderPortal from "components/portal/header-portal";
import { Button } from "ui/index";

/**
 * MultiFileInput - Smart Container Component
 * Handles server uploads and state management for multiple files
 *
 * @param {Array<string>} value - Array of file IDs from server
 * @param {Function} onChange - Callback with array of uploaded file IDs
 * @param {string} accept - File types to accept
 * @param {number} maxFiles - Maximum number of files
 * @param {boolean} disabled - Disable uploads
 * @param {string} className - Additional CSS classes
 * @param {string} error - Error message to display
 */
const MultiFileInput = ({
  value = [],
  onChange,
  accept = "image/*,video/*",
  maxFiles = 10,
  disabled = false,
  className = "",
  error,
  onSubmit,
}) => {
  const baseUrl =
    (typeof import.meta !== "undefined" && import.meta.env?.VITE_BASE_URL) ||
    process.env.NEXT_PUBLIC_BASE_URL ||
    "";
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Sync with external value prop (for form state)
  useEffect(() => {
    if (!value || value.length === 0) {
      setFiles([]);
      return;
    }

    if (Array.isArray(value) && typeof value[0] === "string") {
      // Convert file IDs to file objects for display
      const fileObjects = value.map((id) => ({
        id,
        name: `File ${id}`,
        type: "",
        url: `${baseUrl}/assets/${id}`,
        isImage: true, // Default to image, could enhance with type detection
        isVideo: false,
        isExisting: true,
      }));
      setFiles(fileObjects);
    }
  }, [value]);

  const handleFileChange = async (updatedFiles) => {
    if (disabled || uploading) return;

    // Separate new files (need upload) from existing files
    const newFiles = updatedFiles.filter((f) => f.isNew);
    const existingFiles = updatedFiles.filter((f) => !f.isNew);

    // If only removing files (no new uploads needed)
    if (newFiles.length === 0) {
      setFiles(existingFiles);
      onChange?.(existingFiles.map((f) => f.id));
      return;
    }

    // Upload new files to server
    setUploading(true);
    const uploadedFiles = [];

    for (const fileObj of newFiles) {
      try {
        const formData = new FormData();
        formData.append("file", fileObj.file);
        const uploaded = await fileUpload(formData);

        uploadedFiles.push({
          id: uploaded.id,
          name: fileObj.name,
          type: fileObj.type,
          url: `${baseUrl}/assets/${uploaded.id}`,
          isImage: fileObj.isImage,
          isVideo: fileObj.isVideo,
          isExisting: true,
        });
      } catch (error) {
        console.error("File upload failed:", error);
        // Continue with other files even if one fails
      }
    }

    const allFiles = [...existingFiles, ...uploadedFiles];
    setFiles(allFiles);
    onChange?.(allFiles.map((f) => f.id));
    setUploading(false);
  };

  if (error) {
    return <div className="text-red-500 text-sm">Error: {error}</div>;
  }

  return (
    <div className="relative">
      <HeaderPortal>
        <Button onClick={onSubmit}>Add</Button>
      </HeaderPortal>
      {/* Uploading Overlay */}
      {uploading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            <span className="text-sm text-gray-600">Uploading files...</span>
          </div>
        </div>
      )}

      <MultiFileUpload
        value={files}
        onChange={handleFileChange}
        accept={accept}
        maxFiles={maxFiles}
        disabled={disabled || uploading}
        className={className}
      />
    </div>
  );
};

export default MultiFileInput;
